/**
 * useEffectiveVisibility — the caller's effective DENIED visibility keys
 * (Berechtigungskonzept V3.0 Bereich 3 — the tab-AND-card visibility layer).
 *
 * The web renders Kacheln / Tabs from the api's answer and NEVER recomputes the
 * deny semantics client-side (coding-standards rule #2) — the api gates remain the
 * hard line for sensitive field data (policy-visibility-gate / commission-finance-gate).
 * This hook only shapes the client-detail UI: a hidden card/section is REMOVED
 * (not grayed) via `isHidden(key)`, which mirrors the api's `isVisibilityKeyDenied`
 * (exact-or-ancestor dot-path match: denying a tab hides its cards too).
 *
 * live mode → GET /me/effective-visibility via the typed client.
 * mock mode → empty deny set (every screen renders standalone in tests / dev;
 *             the api guard is what actually enforces at runtime). Same live/mock
 *             split as useEffectivePermissions.
 *
 * [Source: apps/web/src/features/administration/useEffectivePermissions.ts] (live/mock split)
 * [Source: packages/shared/src/visibility.ts#isVisibilityDenied]
 * [Source: apps/api/src/modules/access/effective-visibility.controller.ts]
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isVisibilityDenied } from '@brokly/shared';
import type { EffectiveVisibilityResponse } from '@brokly/shared';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { apiClient } from '../../lib/api-client';

/** Mock-mode fixture — nothing hidden (renders the full client-detail in tests/dev). */
export const EFFECTIVE_VISIBILITY_FIXTURE: EffectiveVisibilityResponse = { denied_keys: [] };

export interface EffectiveVisibility {
  /**
   * True iff the visibility key (or one of its dot-path ancestors) is hidden for
   * the caller — mirrors the api's isVisibilityKeyDenied. Use it to REMOVE a
   * tab/card from the client-detail UI.
   */
  isHidden: (key: string) => boolean;
  /** The raw effective denied-key set (dot-path). */
  denied: ReadonlySet<string>;
  isLoading: boolean;
  isError: boolean;
}

export function useEffectiveVisibility(): EffectiveVisibility {
  const live = isLiveData();
  const query = useQuery<EffectiveVisibilityResponse>({
    queryKey: qk.effectiveVisibility(),
    queryFn: () =>
      live ? apiClient.admin.effectiveVisibility() : Promise.resolve(EFFECTIVE_VISIBILITY_FIXTURE),
    staleTime: live ? 5 * 60_000 : Infinity,
    ...(live ? {} : { initialData: EFFECTIVE_VISIBILITY_FIXTURE }),
  });

  const denied = useMemo(
    () => new Set(query.data?.denied_keys ?? []),
    [query.data],
  );

  const isHidden = (key: string): boolean => isVisibilityDenied(denied, key);

  return {
    isHidden,
    denied,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
